import { Component, Element, Event, EventEmitter, Prop, State, Watch, h } from '@stencil/core';
import DOMPurify from 'dompurify';
import { Disclaimer } from '../types';

@Component({
  tag: 'redirect-popup',
  styleUrl: 'redirect-popup.scss',
  shadow: true
})
export class RedirectPopup {
  @Element() hostEl: HTMLElement;

  @Prop() open: boolean = false;
  @Prop() contentType?: string | null;
  @Prop() redirectTargetUrl: string;
  @Prop() disclaimerContent: Disclaimer;
  @Event({ bubbles: true, composed: true }) confirm: EventEmitter<void>;
  @Event({ bubbles: true, composed: true }) close: EventEmitter<void>;

  @State() shouldRender: boolean = false;

  @Watch('open')
  onOpenChange(newVal: boolean) {
    this.shouldRender = newVal;
  }

  connectedCallback() {
    this.shouldRender = this.open;
  }

  private handleBackdropClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).classList.contains('popup-wrapper')) {
      this.close.emit();
    }
  };

  private handleClose = () => {
    this.close.emit();
  };

  private handleProceed = () => {
    this.confirm.emit();
  };

  private get sanitizedDisclaimer() {
    const content = this.disclaimerContent;
    return {
      title: content?.title ? DOMPurify.sanitize(content.title) : '',
      body: content?.content ? DOMPurify.sanitize(content.content) : '',
      footer: content?.footerContent
        ? DOMPurify.sanitize(content.footerContent)
        : DOMPurify.sanitize(
          'Thank you for visiting https://offers.smartbuy.hdfcbank.com/. Please click here for detailed terms & conditions.'
        ),
      buttonText: content?.buttonText || 'Continue'
    };
  }

  render() {
    if (!this.shouldRender || !this.disclaimerContent) {
      return null;
    }

    const { title, body, footer, buttonText } = this.sanitizedDisclaimer;

    return (
      <div class="popup-wrapper" onClick={this.handleBackdropClick} role="dialog" aria-modal="true" aria-labelledby="popup-title">
        <popup-modal open={true} size="2xl">
          <div class="popup-modal">
            <div class="modal-header">
              <div class="text-left text-xs space-y-4">
                <div class="flex justify-between items-center">
                  {title && (
                    <div class="text-sm font-semibold" innerHTML={title}></div>
                  )}
                  <button class="close-button cursor-pointer" aria-label="Close popup" onClick={this.handleClose}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-body">
              {body && (
                <div class="text-xs text-gray-700" innerHTML={body}></div>
              )}
            </div>

            <div class="modal-footer flex justify-between items-center flex-col md:flex-row gap-3 md:gap-2">
              <div class="text-xs text-gray-700" innerHTML={footer}></div>
              <div>
                <custom-button variant="primary" onButtonClick={this.handleProceed} class="w-full md:w-auto">
                  {buttonText}
                </custom-button>
              </div>
            </div>
          </div>
        </popup-modal>
      </div>
    );
  }
}
